/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Upload, Mail, KeyRound } from "lucide-react";

export default function Settings() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const fileRef = useRef();
  const [company, setCompany] = useState({ name: "", cif: "", address: "", phone: "", email: "", iban: "", entity_type: "juridica" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [followup, setFollowup] = useState(true);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error(t("settings.pwdMin"));
    if (pwd.new_password !== pwd.confirm) return toast.error(t("settings.pwdMismatch"));
    setPwdBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(r.data.message || t("settings.pwdChanged"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("settings.pwdError"));
    } finally {
      setPwdBusy(false);
    }
  };

  const toggleFollowup = async () => {
    const next = !followup;
    setFollowup(next);
    try {
      await api.put("/settings/followups", { enabled: next });
      toast.success(next ? t("settings.followupOn") : t("settings.followupOff"));
      refresh();
    } catch (e) {
      setFollowup(!next);
      toast.error(t("settings.saveError"));
    }
  };

  useEffect(() => {
    if (user) {
      setCompany({
        name: user.company?.name || "",
        cif: user.company?.cif || "",
        address: user.company?.address || "",
        phone: user.company?.phone || "",
        email: user.company?.email || "",
        iban: user.company?.iban || "",
        entity_type: user.company?.entity_type || "juridica",
      });
      if (user.logo_path) {
        const token = localStorage.getItem("offerai_token");
        setLogoUrl(`${API}/company/logo?auth=${token}&t=${Date.now()}`);
      }
      setFollowup(user.auto_followup !== false);
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company", company);
      toast.success(t("settings.companySaved"));
      refresh();
    } catch (e) {
      toast.error(t("settings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(t("settings.logoUploaded"));
      const token = localStorage.getItem("offerai_token");
      setLogoUrl(`${API}/company/logo?auth=${token}&t=${Date.now()}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("settings.uploadError"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <div className="overline">{t("settings.overline")}</div>
      <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("settings.title")}</h1>
      <p className="text-sm text-[#94A3B8] mt-2">{t("settings.subtitle")}</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo */}
        <div className="card-flat p-6">
          <div className="overline mb-4">{t("settings.logoTitle")}</div>
          <div className="border-2 border-dashed border-[#1E293B] p-6 flex flex-col items-center justify-center min-h-[160px]" data-testid="logo-preview">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-32 max-w-full object-contain"/>
            ) : (
              <div className="text-center text-[#94A3B8] text-sm">{t("settings.noLogo")}</div>
            )}
          </div>
          <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={uploadLogo} data-testid="logo-file-input"/>
          <button type="button" onClick={()=>fileRef.current?.click()} disabled={uploading} className="btn-outline w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upload-logo-btn">
            <Upload size={16}/> {uploading ? t("settings.uploading") : t("settings.uploadLogo")}
          </button>
          <p className="text-xs text-[#94A3B8] mt-2">{t("settings.logoHint")}</p>
        </div>

        {/* Company form */}
        <form onSubmit={save} className="lg:col-span-2 card-flat p-6 space-y-3" data-testid="company-form">
          <div className="overline mb-2">{t("settings.companyDetails")}</div>

          {/* Entity type selector */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("settings.type")}</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" data-testid="entity-juridica"
                onClick={()=>setCompany({...company, entity_type: "juridica"})}
                className={`px-3 py-2.5 text-sm font-semibold border transition-colors ${company.entity_type === "juridica" ? "bg-[#00BFFF] text-white border-[#00BFFF]" : "bg-[#0B111D] text-[#E2E8F0] border-[#1E293B] hover:border-[#00BFFF]"}`}>
                {t("settings.legalEntity")}
              </button>
              <button type="button" data-testid="entity-fizica"
                onClick={()=>setCompany({...company, entity_type: "fizica"})}
                className={`px-3 py-2.5 text-sm font-semibold border transition-colors ${company.entity_type === "fizica" ? "bg-[#00BFFF] text-white border-[#00BFFF]" : "bg-[#0B111D] text-[#E2E8F0] border-[#1E293B] hover:border-[#00BFFF]"}`}>
                {t("settings.physicalPerson")}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{company.entity_type === "fizica" ? t("settings.nameFizica") : t("settings.nameJuridica")}</label>
            <input data-testid="company-name" className="input-flat mt-1" value={company.name} onChange={(e)=>setCompany({...company, name: e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{company.entity_type === "fizica" ? t("settings.cnpOptional") : t("settings.cifCui")}</label>
              <input data-testid="company-cif" className="input-flat mt-1" value={company.cif} onChange={(e)=>setCompany({...company, cif: e.target.value})}/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("settings.phone")}</label>
              <input data-testid="company-phone" className="input-flat mt-1" value={company.phone} onChange={(e)=>setCompany({...company, phone: e.target.value})}/>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("settings.address")}</label>
            <input data-testid="company-address" className="input-flat mt-1" value={company.address} onChange={(e)=>setCompany({...company, address: e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("settings.companyEmail")}</label>
              <input data-testid="company-email" className="input-flat mt-1" value={company.email} onChange={(e)=>setCompany({...company, email: e.target.value})}/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{t("settings.iban")}</label>
              <input data-testid="company-iban" className="input-flat mt-1" value={company.iban} onChange={(e)=>setCompany({...company, iban: e.target.value})}/>
            </div>
          </div>
          <div className="pt-3">
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-company-btn">
              {saving ? t("settings.saving") : t("settings.save")}
            </button>
          </div>
        </form>
      </div>

      {/* Auto follow-up reminders */}
      <div className="mt-6 card-flat p-6" data-testid="followup-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center shrink-0">
              <Mail size={18} className="text-[#00BFFF]" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">{t("settings.followupTitle")}</div>
              <p className="text-sm text-[#94A3B8] mt-1 max-w-md">{t("settings.followupDesc")}</p>
            </div>
          </div>
          <button type="button" onClick={toggleFollowup} data-testid="followup-toggle"
            className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${followup ? "bg-[#00BFFF]" : "bg-[#1E293B]"}`}>
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${followup ? "translate-x-6" : "translate-x-0"}`} />
          </button>
        </div>
      </div>

      {/* Change password */}
      <div className="mt-6 card-flat p-6" data-testid="change-password-card">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center shrink-0">
            <KeyRound size={18} className="text-[#00BFFF]" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{t("settings.pwdTitle")}</div>
            <p className="text-sm text-[#94A3B8] mt-1">{t("settings.pwdDesc")}</p>
          </div>
        </div>
        <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="overline block mb-2">{t("settings.currentPwd")}</label>
            <input type="password" required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="input-flat" placeholder="••••••••" data-testid="current-password-input" />
          </div>
          <div>
            <label className="overline block mb-2">{t("settings.newPwd")}</label>
            <input type="password" required value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="input-flat" placeholder="••••••••" data-testid="new-password-input" />
          </div>
          <div>
            <label className="overline block mb-2">{t("settings.confirmPwd")}</label>
            <input type="password" required value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="input-flat" placeholder="••••••••" data-testid="confirm-password-input" />
          </div>
          <div className="md:col-span-3">
            <button type="submit" disabled={pwdBusy} className="btn-primary disabled:opacity-50" data-testid="change-password-submit">
              {pwdBusy ? t("settings.saving") : t("settings.pwdChangeBtn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
